import { Fragment, useState } from "react";
import { ChevronDown, ChevronRight, EyeOff, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { CspmFinding } from "@/types/api";

interface FindingsTableProps {
  findings: CspmFinding[];
  isLoading: boolean;
  onSuppress?: (findingId: string) => void;
  onUnsuppress?: (findingId: string) => void;
}

const severityColors: Record<string, string> = {
  critical: "bg-red-500/10 text-red-500 border-red-500/30",
  high: "bg-orange-500/10 text-orange-500 border-orange-500/30",
  medium: "bg-yellow-500/10 text-yellow-500 border-yellow-500/30",
  low: "bg-blue-500/10 text-blue-500 border-blue-500/30",
};

const providerLabels: Record<string, string> = {
  aws: "AWS",
  azure: "Azure",
  gcp: "GCP",
};

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function FindingsTable({
  findings,
  isLoading,
  onSuppress,
  onUnsuppress,
}: FindingsTableProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 animate-pulse rounded bg-muted" />
        ))}
      </div>
    );
  }

  if (findings.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        No findings found
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8" />
          <TableHead>Severity</TableHead>
          <TableHead>Title</TableHead>
          <TableHead>Cloud</TableHead>
          <TableHead>Resource</TableHead>
          <TableHead>Region</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Last Seen</TableHead>
          <TableHead className="w-8" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {findings.map((f) => (
          <Fragment key={f.id}>
            <TableRow
              key={f.id}
              className="cursor-pointer"
              onClick={() => setExpandedId(expandedId === f.id ? null : f.id)}
            >
              <TableCell>
                {expandedId === f.id ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </TableCell>
              <TableCell>
                <Badge
                  variant="outline"
                  className={severityColors[f.severity] || ""}
                >
                  {f.severity}
                </Badge>
              </TableCell>
              <TableCell className="max-w-xs truncate font-medium">
                {f.title}
              </TableCell>
              <TableCell>
                <span className="text-xs font-medium uppercase">
                  {providerLabels[f.cloud_provider] || f.cloud_provider}
                </span>
              </TableCell>
              <TableCell className="max-w-[200px] truncate text-xs font-mono">
                {f.resource_id}
              </TableCell>
              <TableCell className="text-sm">{f.region || "-"}</TableCell>
              <TableCell>
                <Badge variant="secondary" className="text-xs">
                  {f.status}
                </Badge>
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {formatDate(f.last_seen_at)}
              </TableCell>
              <TableCell>
                {f.status === "open" && onSuppress && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSuppress(f.id);
                    }}
                    title="Suppress"
                  >
                    <EyeOff className="h-4 w-4" />
                  </Button>
                )}
                {f.status === "suppressed" && onUnsuppress && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      onUnsuppress(f.id);
                    }}
                    title="Unsuppress"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
            {expandedId === f.id && (
              <TableRow key={`${f.id}-detail`}>
                <TableCell colSpan={9} className="bg-muted/30 px-8 py-4">
                  <div className="space-y-3 text-sm">
                    {f.description && (
                      <div>
                        <span className="font-medium">Description: </span>
                        {f.description}
                      </div>
                    )}
                    {f.remediation && (
                      <div>
                        <span className="font-medium">Remediation: </span>
                        {f.remediation}
                      </div>
                    )}
                    {Object.keys(f.details).length > 0 && (
                      <div>
                        <span className="font-medium">Details: </span>
                        <pre className="mt-1 rounded bg-muted p-2 text-xs overflow-auto max-h-48">
                          {JSON.stringify(f.details, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </Fragment>
        ))}
      </TableBody>
    </Table>
  );
}
